import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ContentChild,
  EventEmitter,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from 'primeng/autocomplete';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import {
  cleanupOverlaysOnNavigation,
  removeOrphanedOverlays,
} from '../../../utilities/overlay-cleanup';
import { BaseControlComponent } from './base-control.component';
import { FormFieldComponent } from './form-field.component';

@Component({
  selector: 'app-autocomplete',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    FormFieldComponent,
    AutoCompleteModule,
    IconFieldModule,
    InputIconModule,
    InputGroupModule,
    InputGroupAddonModule,
  ],
  template: `
    <app-form-field
      [control]="control"
      [id]="id"
      [label]="label"
      [helpText]="helpText"
      [showHelpText]="showHelpText"
      class="tw-w-full"
    >
      <div class="tw-flex tw-justify-end tw-items-center tw-w-full">
        <p-autocomplete
          #pAutocomplete
          class="tw-w-full"
          [formControl]="control"
          [id]="id"
          [fluid]="true"
          [class]="{
            'ng-invalid': touchedInvalid,
            'ng-dirty': touchedInvalid,
          }"
          [group]="group"
          (completeMethod)="search($event)"
          [suggestions]="suggestions"
          [forceSelection]="true"
          (onSelect)="select.emit($event.value)"
          [inputStyle]="inputStyle"
          [optionLabel]="optionLabel"
          [autoOptionFocus]="true"
          [delay]="0"
          [scrollHeight]="scrollHeight"
          [placeholder]="placeholder"
          ariaLabel="Search and filter table content"
          [ariaRequired]="required"
        >
          <ng-template let-item pTemplate="item">
            <ng-container
              [ngTemplateOutlet]="template ?? defaultTemplate"
              [ngTemplateOutletContext]="{ item }"
            >
            </ng-container>
          </ng-template>
        </p-autocomplete>
      </div>
    </app-form-field>

    <ng-template #defaultTemplate let-item="item">
      <span
        [style]="{
          'font-weight': item === control.value ? 'bold' : 'normal',
        }"
      >
        {{ optionLabel ? item[optionLabel] : item }}
      </span>
    </ng-template>
  `,
  styles: ``,
})
export class AutocompleteComponent
  extends BaseControlComponent
  implements AfterViewInit
{
  private router = inject(Router);

  /** Selector for the PrimeNG autocomplete overlay this component owns. */
  private static readonly OVERLAY_SELECTOR = '.p-autocomplete-overlay';

  @ContentChild(TemplateRef)
  template: TemplateRef<any>;

  @ViewChild('pAutocomplete')
  private pAutocomplete?: AutoComplete;

  @Input()
  inputStyle: any;

  @Input()
  suggestions: any[];

  @Input()
  optionLabel: string;

  @Input()
  group = false;

  @Input()
  scrollHeight = '240px';

  @Output()
  filter = new EventEmitter<AutocompleteFilterEvent>();

  @Output()
  select = new EventEmitter<any>();

  search(event: AutoCompleteCompleteEvent) {
    this.filter.emit({
      autocomplete: this,
      value: event.query,
    });
  }

  ngAfterViewInit(): void {
    /*
     * WORKAROUND: PrimeNG autocomplete panels appended to <body> can be
     * left behind in the DOM when the host is destroyed mid-animation
     * (e.g. router navigation while suggestions are open on mobile).
     * Hide on NavigationStart and sweep any stragglers.
     */
    this.dispose.subs = cleanupOverlaysOnNavigation(this.router, () =>
      this.cleanupOverlays(),
    );
  }

  override ngOnDestroy(): void {
    this.cleanupOverlays();
    super.ngOnDestroy();
  }

  /**
   * Hide PrimeNG autocomplete overlay and remove any DOM remnants.
   * Safe to call multiple times.
   */
  private cleanupOverlays(): void {
    this.pAutocomplete?.hide();
    removeOrphanedOverlays(AutocompleteComponent.OVERLAY_SELECTOR);
  }
}

export class AutocompleteFilterEvent {
  autocomplete: AutocompleteComponent;
  value: string;
}
